import React, { useEffect, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router";
import { Button, Col, Collapse, Divider, Form, Row, Tooltip } from "antd";
import { LeftOutlined } from "@ant-design/icons";
import { round } from "lodash";
import moment from "moment";
import { useForm } from "antd/lib/form/Form";
import { useAppDispatch, useAppSelector } from "../../..";
import {
  createOfferPrice,
  fetchOfferPrice,
  offeringActions,
} from "../../../store/slices/offering/offering";
import {
  checkIsLoading,
  toHttpFormatEndOfDay,
  toHttpFormatStartOfDay,
  ymdDateFormat,
} from "../../../lib/utils";
import { OfferPriceParamsEdit } from "../../../components/OfferPrice/OfferPriceParamsEdit/OfferPriceParamsEdit";
import { IOfferPrice, IOfferPriceCreateParams } from "../../../lib/types/OfferPrice";
import { CharacteristicsContainer } from "../../../modules/policies";
import { useCharacteristicsInitialisation } from "../../../modules/policies/pages/CharacteristicsContainer/utils/useCharacteristicsInitialisation";
import { getCharFormatItem } from "../../../modules/policies/services/getCharFormatItem";
import { useOfferContext } from "../context/OfferContext";
import "./OfferPriceCreateContainer.less";

export const OfferPriceCreateContainer = () => {
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { search } = useLocation();
  const { offerId, priceId } = useParams<{ offerId: string; priceId?: string }>();
  const offerOnDate = new URLSearchParams(search).get("onDate")?.replace(" ", "+");
  const onDatePrice = new URLSearchParams(search).get("onDatePrice")?.replace(" ", "+");

  const { reloadOffer } = useOfferContext();

  const [offerPriceParamsForm] = useForm();

  const { offerPrice, createOfferPriceLoading } = useAppSelector((state) => state.offering);
  const { offer } = useAppSelector((state) => state.offering);
  const { tax } = useAppSelector((state) => state.taxTs);

  const { tenant } = useAppSelector((state) => state.tenants);
  const { aggregationRules } = useAppSelector((state) => state.aggregationRules);
  const currencyId = tenant.defaultCurrencyId;

  const toOfferId = () => {
    if (history.length < 3) {
      history.push(`/offer/${offerId}?onDate=${offerOnDate}`);
    } else {
      history.goBack();
    }
  };

  // ---> Характеристики <---
  const {
    fromDateCharacteristic,
    onChangeFromDateCharacteristic,
    onCheckFieldsError,
    fieldsError,
    infoCharacteristic,
    charValuesCharacteristic,
    setInfoCharacteristic,
    setCharValuesCharacteristic,
  } = useCharacteristicsInitialisation({
    initialFromDate: offerPriceParamsForm.getFieldValue("fromDate"),
  });
  // ---> Характеристики <---

  const onFinish = (values) => {
    const isValid = onCheckFieldsError(["infoCharacteristic", "charValuesCharacteristic"]);

    if (!isValid) {
      return;
    }

    const info = infoCharacteristic.map((el) => getCharFormatItem(el, 1035));
    const charValues = charValuesCharacteristic.map((el) => getCharFormatItem(el));

    const params: IOfferPriceCreateParams = {
      // Данные из формы
      aggregationRuleId: values.aggregationRuleId,
      price: values.price,
      fromDate: toHttpFormatStartOfDay(values.fromDate),
      toDate: values.toDate ? toHttpFormatEndOfDay(values.toDate) : undefined,
      name: values.name,
      rateModeId: values.rateModeId,
      priceEventId: values.priceEventId,
      ratePeriodId: values.ratePeriodId,
      rateTypeId: values.rateTypeId,
      currencyId: values.currencyId,
      chargeNameId: values.chargeNameId,
      priceUnitId: values.priceUnitId,
      conditionTypes: values.conditionTypes,
      conditionTypeId: values.conditionTypes?.reduce((acc, cur) => acc + cur) || 0,
      dsc: values?.dsc,
      rangeFrom: values?.rangeFrom,
      rangeTo: values?.rangeTo,
      // Дополнительные данные
      offeringId: Number(offerId),
      info,
      charValues,
    };

    dispatch(createOfferPrice(params)).then((res) => {
      if (res?.payload) {
        const { id, fromDate } = res.payload as IOfferPrice;
        const onDateCreatedPrice = moment(fromDate).format("YYYY-MM-DD");
        if (moment(fromDate, ymdDateFormat).isSame(moment(offerOnDate, ymdDateFormat), "day")) {
          reloadOffer();
        }
        history.replace(
          `/offer/${offerId}/price/${id}?onDate=${onDateCreatedPrice}&onDatePrice=${onDateCreatedPrice}`
        );
      }
    });
  };

  // При копировании для предзаполнения запрашиваем данные цены
  useEffect(() => {
    if (priceId && onDatePrice) {
      dispatch(fetchOfferPrice({ rateId: priceId, onDate: toHttpFormatStartOfDay(onDatePrice) }));
      return () => {
        dispatch(offeringActions.cleanOfferPrice());
      };
    }
  }, [priceId, onDatePrice]);

  // Исходные значения формы
  useEffect(() => {
    if (offerPrice && priceId) {
      const price = Number(offerPrice?.price) || 0;
      const taxRate = tax?.taxRate || "0";
      const taxMultiplier = Number(taxRate) / 100 + 1;

      const priceWithVat = round(price * taxMultiplier);

      offerPriceParamsForm.setFieldsValue({
        aggregationRuleId: offerPrice?.aggregationRuleId,
        price: offerPrice?.price,
        toDate: moment(offerPrice?.toDate),
        // fromDate: moment(offerPrice?.fromDate),
        name: offerPrice?.name,
        rateModeId: offerPrice?.rateModeId,
        priceEventId: offerPrice?.priceEventId,
        // ratePeriodId: offerPrice?.ratePeriodId,
        rateTypeId: offerPrice?.rateTypeId,
        currencyId: offerPrice?.currencyId,
        chargeNameId: offerPrice?.chargeNameId,
        priceUnitId: offerPrice?.priceUnitId,
        conditionTypes: offerPrice?.conditionTypes,
        // conditionTypeId: offerPrice?.conditionTypeId,
        // dsc: offerPrice?.dsc,
        rangeFrom: offerPrice?.rangeFrom,
        rangeTo: offerPrice?.rangeTo,
        priceWithVat,
      });

      // setInfoCharacteristic([]);А
      // setCharValuesCharacteristic([]);
    } else if (!priceId && offer && currencyId) {
      offerPriceParamsForm.setFieldsValue({
        fromDate:
          offer?.fromDate && moment().startOf("d").isAfter(moment(offer?.fromDate).startOf("d"))
            ? moment()
            : moment(offer?.fromDate),
        priceEventId: 1,
        currencyId,
        rateTypeId: 1,
      });
    }
  }, [priceId, offerPrice, offer, currencyId]);

  return (
    <div className="offer-price-create-container">
      <h1>
        <Tooltip title="К просмотру предложения">
          <LeftOutlined onClick={toOfferId} className="offer-price-create-container__backBtn" />
        </Tooltip>
        {`Создание цены для предложения ${offer?.extName || ""} ${
          offer?.name ? `(${String(offer?.name)})` : ""
        }`}
      </h1>
      <Divider className="offer-price-create-container__upperDivider" />
      <Form form={offerPriceParamsForm} labelAlign="left" onFinish={onFinish}>
        <OfferPriceParamsEdit
          viewMode="create"
          offerPriceParamsForm={offerPriceParamsForm}
          offer={offer}
          onChangeFromDateCharacteristic={onChangeFromDateCharacteristic}
          aggregationRules={aggregationRules}
        />
      </Form>

      <CharacteristicsContainer
        objectTypeId={1035}
        mode="create"
        title="Характеристики цены"
        tableData={infoCharacteristic}
        setTableData={setInfoCharacteristic}
        dataSaved={offerPrice?.info}
        fromDateCharacteristic={fromDateCharacteristic}
        messageWarningNotFromDate="Необходимо выбрать Дату создания цены"
        fieldsError={fieldsError}
      />
      <CharacteristicsContainer
        objectTypeId={1032}
        mode="create"
        title="Параметры доступности цены"
        isInheritableObject
        newObjectChars={offer?.productCharValues}
        isNewObjectChars
        tableData={charValuesCharacteristic}
        setTableData={setCharValuesCharacteristic}
        dataSaved={offerPrice?.charValues}
        fromDateCharacteristic={fromDateCharacteristic}
        messageWarningNotFromDate="Необходимо выбрать Дату создания цены"
        disabledValidation
        isDefaultDisabled
        fieldsError={fieldsError}
      />

      <Collapse bordered={false} defaultActiveKey="1">
        <Collapse.Panel
          key="1"
          header={
            <Row justify="space-between" className="offer-price-create-container__pageTitle">
              <h3>Выражение</h3>
            </Row>
          }
        />
      </Collapse>
      <Divider style={{ margin: 0 }} />
      <Collapse bordered={false} defaultActiveKey="1">
        <Collapse.Panel
          key="1"
          header={
            <Row justify="space-between" className="offer-price-create-container__pageTitle">
              <h3>Тарифное правило</h3>
            </Row>
          }
        />
      </Collapse>
      <Divider style={{ margin: 0 }} />
      <Row align="middle" style={{ paddingTop: "10px" }} justify="center">
        <Col
          span={24}
          style={{
            textAlign: "center",
          }}
        >
          <Button
            style={{
              margin: "0 8px",
            }}
            onClick={toOfferId}
          >
            Отмена
          </Button>
          <Button
            type="primary"
            onClick={() => {
              offerPriceParamsForm.submit();
            }}
            loading={checkIsLoading(createOfferPriceLoading)}
          >
            Создать
          </Button>
        </Col>
      </Row>
    </div>
  );
};
